import {
  FloatType,
  Maybe,
  TileStatus,
  TileType,
  Typenames,
  VxlTrackerCard,
  VxlTrackerCardTile,
  VxlTrackerProgressResult,
} from '@varo/graphql-schema-types';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import { TrackerCardProps } from 'shared/components/vxl/cards/TrackerCard/TrackerCard';
import {
  ComplexTrackerProps,
  SimpleTrackerProps,
} from 'shared/components/vxl/Tracker/Tracker';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';
import { getVXLColor } from 'shared/utils/getVXLColor/getVXLColor';
import { isNotNil } from 'shared/utils/isNotNil/isNotNil';

interface CardTrackerTileNode extends VXLComponent {
  id: string;
  name: VXLComponentId.TRACKER_CARD;
  props: TrackerCardProps;
}

/**
 * we need to alias `card` to `trackerCard` in our home tiles queries to prevent
 * type conflicts. extend this interface to account for this in the type system.
 */
export interface ExtendedVxlTrackerCardTile
  extends Omit<VxlTrackerCardTile, 'card'> {
  trackerCard?: VxlTrackerCard | undefined;
}

const cardTrackerTile = (
  tile: ExtendedVxlTrackerCardTile,
  { navigateToRoute }: HomeCardDependencies,
): Maybe<CardTrackerTileNode> => {
  const { trackerCard } = tile;

  if (
    tile.status !== TileStatus.Loaded ||
    tile.tileType !== TileType.VxlTrackerCardTile ||
    !trackerCard
  ) {
    return null;
  }
  const { progressValue, backgroundTextureV2: backgroundTexture } =
    trackerCard.trackerProps;

  const isFloatType = (value: VxlTrackerProgressResult): value is FloatType =>
    (value as FloatType).__typename === Typenames.FloatType;

  const progressValueFloat = isFloatType(progressValue)
    ? progressValue.float
    : 0;

  const onPress: Maybe<() => void> = isNotNil(trackerCard.onPress)
    ? () => {
        const action = trackerCard.onPress;
        if (action) {
          navigateToRoute(action);
        }
      }
    : null;

  const nudgeOnPress: Maybe<() => void> = isNotNil(trackerCard.nudge?.onPress)
    ? () => {
        const action = trackerCard.nudge?.onPress;
        if (action) {
          navigateToRoute(action);
        }
      }
    : null;

  const trackerProps: SimpleTrackerProps | ComplexTrackerProps = isFloatType(
    progressValue,
  )
    ? ({
        trackerType: 'SIMPLE',
        hasAnimation: false,
        progressValue: progressValueFloat,
        backgroundTexture,
        fillColor: trackerCard.trackerProps.fillColor
          ? getVXLColor(trackerCard.trackerProps.fillColor)
          : undefined,
      } satisfies SimpleTrackerProps)
    : ({
        trackerType: 'COMPLEX',
        hasAnimation: false,
        steps: progressValue.steps.map((step) => ({ length: step })),
        fillToStep: progressValue.fillToStep,
        backgroundTexture,
        fillColor: trackerCard.trackerProps.fillColor
          ? getVXLColor(trackerCard.trackerProps.fillColor)
          : undefined,
      } satisfies ComplexTrackerProps);

  const nudge = trackerCard.nudge
    ? {
        nudgeTitleText: trackerCard.nudge.titleText ?? '',
        nudgeBodyText: trackerCard.nudge.bodyText ?? '',
        nudgeButtonText: trackerCard.nudge.buttonText ?? '',
        nudgeChevron: trackerCard.nudge.showsChevron ?? false,
        nudgeOnPress: nudgeOnPress ?? undefined,
        nudgeIcon: trackerCard.nudge.icon ?? '',
      }
    : undefined;

  return {
    id: tile.id,
    name: VXLComponentId.TRACKER_CARD,
    props: {
      titleText: trackerCard.titleText,
      leadingText: trackerCard.leadingText ?? '',
      trailingText: trackerCard.trailingText ?? '',
      cardOnPress: onPress ?? undefined,
      nudge,
      leadingLabel: trackerCard.leadingLabel ?? '',
      trailingLabel: trackerCard.trailingLabel ?? '',
      trackerProps,
    },
  };
};

export default cardTrackerTile;
